import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import MasonryProducts from "../components/MasonryProducts";
import Header from "../components/layout/Header";
import CategoryList from "../components/CategoryList";
import axios from "axios";
import Footer from "../components/layout/Footer";
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL;

export default function Home() {
  const [ads, setAds] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const loadAds = async () => {
      try {
        const res = await axios.get(`${apiUrl}/ads`);
        setAds(res.data);
      } catch (err) {
        console.log("Failed loading ads:", err);
      }
    };
    loadAds();
  }, []);

  const goTo = useCallback((idx) => {
    setCurrentSlide(idx);
  }, []);

  const next = useCallback(() => {
    if (ads.length === 0) return;
    setCurrentSlide((p) => (p + 1) % ads.length);
  }, [ads.length]);

  const prev = useCallback(() => {
    if (ads.length === 0) return;
    setCurrentSlide((p) => (p - 1 + ads.length) % ads.length);
  }, [ads.length]);

  // Auto-advance timer
  useEffect(() => {
    if (isPaused || ads.length <= 1) return;
    const current = ads[currentSlide];
    const delay = current?.type === "video" ? 15000 : 5000;
    timerRef.current = setTimeout(next, delay);
    return () => clearTimeout(timerRef.current);
  }, [currentSlide, isPaused, ads, next]);

  const currentAd = ads[currentSlide];

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#f4faf7] via-[#fafbfc] to-[#f4f7f5] text-gray-900">
      {/* Navbar */}
      <Header />

      {/* Hero Section Banner */}
      <section className="max-w-screen-2xl mx-auto px-6 pt-8 pb-12">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-600/10 via-emerald-500/5 to-transparent border border-green-500/10 px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center shadow-sm">
          {/* Subtle colored blur lights behind the text */}
          <div className="absolute top-[-20%] left-[-10%] w-[300px] h-[300px] rounded-full bg-green-300/20 blur-[80px] pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-emerald-400/10 blur-[100px] pointer-events-none" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 relative z-10"
          >
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 uppercase tracking-wider shadow-sm w-fit">
              ✨ Premium Marketplace
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-gray-900">
              Welcome to <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">HuluOrder</span>
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed max-w-md">
              Your one-stop marketplace for electronics, fashion, home goods, and more. Find verified items and start ordering instantly.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="#latestProducts"
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg shadow-green-600/20 hover:shadow-green-700/30 transition-all duration-200"
              >
                Browse Items
              </Link>
              <Link
                to="/PostItemPage"
                className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300 font-bold rounded-xl shadow-sm transition-all duration-200"
              >
                Sell an Item
              </Link>
            </div>
          </motion.div>

          {/* AD CAROUSEL */}
          <div className="relative w-full h-80 overflow-hidden rounded-2xl shadow-xl bg-black group z-10 border border-white/10">
            <AnimatePresence mode="wait">
              {currentAd && (
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                >
                  {currentAd.type === "video" ? (
                    <video
                      ref={videoRef}
                      src={currentAd.url}
                      className="w-full h-full object-cover"
                      autoPlay
                      loop
                      muted={isMuted}
                      playsInline
                    />
                  ) : (
                    <img
                      src={currentAd.url}
                      alt="Advertisement"
                      className="w-full h-full object-cover"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Gradient overlay for controls */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

            {/* Navigation Arrows */}
            {ads.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Bottom controls bar */}
            <div className="absolute bottom-0 left-0 right-0 px-4 py-3 flex items-center justify-between">
              {/* Dots */}
              <div className="flex gap-2">
                {ads.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === currentSlide
                      ? "w-6 bg-white"
                      : "w-1.5 bg-white/50 hover:bg-white/80"
                      }`}
                  />
                ))}
              </div>

              {/* Media controls */}
              <div className="flex items-center gap-2">
                {currentAd?.type === "video" && (
                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                )}
                <button
                  onClick={() => setIsPaused(!isPaused)}
                  className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition"
                >
                  {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-center gap-3 mb-8 ml-4">
          <span className="w-1.5 h-7 bg-green-600 rounded-full" />
          <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">Shop by Category</h3>
        </div>
        <CategoryList />
      </section>

      {/* Masonry Product Grid */}
      <section className="max-w-screen-2xl mx-auto px-6 pb-20" id="latestProducts">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-1.5 h-7 bg-green-600 rounded-full" />
          <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">Latest Items</h3>
        </div>
        <MasonryProducts />
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
